import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import QRCode from 'react-qr-code';
import { Smartphone, Monitor, Tablet, ArrowLeftRight, QrCode, Link, CheckCircle, Clock, Signal, Download } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageLayout } from '@/components/layout/PageLayout';
import { toast } from 'sonner';
import {
  getOrCreateHandoffDeviceId,
  registerHandoffDevice,
  pingHandoffDevice,
  listHandoffDevices,
  listHandoffSessions,
  sendHandoff,
  listIncomingHandoffs,
  receiveHandoff,
  type HandoffDeviceDTO,
  type HandoffSessionDTO,
  type TabSummaryDTO,
} from '../services/handoff-api';
import { tabService } from '../services/tabs';
import { resolveWorkspaceSessionId } from '../services/workspaceSession';

interface Device {
  id: string;
  name: string;
  type: 'desktop' | 'mobile' | 'tablet';
  status: 'online' | 'offline';
  lastSeen: string;
  tabs: number;
}

interface HandoffSessionRow {
  id: string;
  fromDevice: string;
  toDevice: string;
  tabs: HandoffTabSummary[];
  timestamp: string;
  status: string;
}

interface HandoffTabSummary {
  title: string;
  url: string;
}

const IMPORTED_HANDOFF_TABS_STORAGE_KEY = 'khiimori_handoff_imported_tabs';

function handoffTabFingerprint(tab: Pick<HandoffTabSummary, 'title' | 'url'>): string {
  return `${tab.title.trim()}|${tab.url.trim()}`;
}

function getImportedHandoffFingerprints(deviceId: string): Set<string> {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(`${IMPORTED_HANDOFF_TABS_STORAGE_KEY}:${deviceId}`);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((v): v is string => typeof v === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function rememberImportedHandoffTabs(deviceId: string, tabs: HandoffTabSummary[]): void {
  if (typeof localStorage === 'undefined' || tabs.length === 0) return;
  const existing = getImportedHandoffFingerprints(deviceId);
  for (const tab of tabs) {
    existing.add(handoffTabFingerprint(tab));
  }
  localStorage.setItem(`${IMPORTED_HANDOFF_TABS_STORAGE_KEY}:${deviceId}`, JSON.stringify([...existing]));
}

async function loadOpenTabsForHandoff(deviceId: string): Promise<TabSummaryDTO[]> {
  const sessionId = await resolveWorkspaceSessionId();
  if (!sessionId) return [];

  const importedFingerprints = getImportedHandoffFingerprints(deviceId);
  const { tabs } = await tabService.getTabsBySession(sessionId, 1, 100);
  return tabs
    .filter((t) => t.status !== 'closed' && t.url?.trim())
    .map((t) => ({
      title: t.title?.trim() || t.url,
      url: t.url.trim(),
    }))
    .filter((t) => !importedFingerprints.has(handoffTabFingerprint(t)));
}

function tabsFromSession(s: HandoffSessionDTO): HandoffTabSummary[] {
  const tab = s.tab;
  if (!tab) return [];
  if (tab.open_tabs?.length) {
    return tab.open_tabs
      .map((x) => ({
        title: x.title?.trim() || x.url?.trim() || 'Tab',
        url: x.url?.trim() || '',
      }))
      .filter((x) => x.title);
  }
  if (tab.title?.trim() || tab.url?.trim()) {
    return [{ title: tab.title?.trim() || tab.url!.trim(), url: tab.url?.trim() || '' }];
  }
  return [];
}

function mapApiDeviceToCard(d: HandoffDeviceDTO): Device {
  const t = d.type as Device['type'];
  const type: Device['type'] =
    t === 'mobile' || t === 'tablet' || t === 'desktop' ? t : 'desktop';
  return {
    id: d.id,
    name: d.name,
    type,
    status: d.is_online ? 'online' : 'offline',
    lastSeen: d.last_seen,
    tabs: 0,
  };
}

function mapSessionToRow(s: HandoffSessionDTO, nameById: Map<string, string>): HandoffSessionRow {
  return {
    id: s.id,
    fromDevice: nameById.get(s.source_device) ?? s.source_device,
    toDevice: nameById.get(s.target_device) ?? s.target_device,
    tabs: tabsFromSession(s),
    timestamp: s.created_at,
    status: s.status,
  };
}

function HandoffTabList({ tabs, limit }: { tabs: HandoffTabSummary[]; limit?: number }) {
  if (tabs.length === 0) {
    return <p className="text-sm text-khiimori-text-muted">No tab data in this handoff.</p>;
  }

  const visible = limit ? tabs.slice(0, limit) : tabs;
  return (
    <ul className="mt-2 space-y-1.5">
      {visible.map((tab, idx) => (
        <li key={`${tab.url}-${idx}`} className="text-sm">
          {tab.url ? (
            <a
              href={tab.url}
              target="_blank"
              rel="noreferrer"
              className="text-primary-400 hover:text-primary-300 hover:underline"
            >
              {tab.title}
            </a>
          ) : (
            <span className="text-khiimori-text-primary/80">{tab.title}</span>
          )}
        </li>
      ))}
      {limit && tabs.length > limit ? (
        <li className="text-xs text-khiimori-text-muted">+{tabs.length - limit} more</li>
      ) : null}
    </ul>
  );
}

const TabHandoff: React.FC = () => {
  const [localDeviceId] = useState(() => getOrCreateHandoffDeviceId());
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [showQR, setShowQR] = useState(false);
  const [devices, setDevices] = useState<Device[]>([]);
  const [handoffSessions, setHandoffSessions] = useState<HandoffSessionRow[]>([]);
  const [incoming, setIncoming] = useState<HandoffSessionDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const pairUrl = useMemo(
    () => `${typeof window !== 'undefined' ? window.location.origin : ''}/tab-handoff?register=1`,
    []
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      await registerHandoffDevice({
        id: localDeviceId,
        name: `Device ${localDeviceId.slice(0, 8)}`,
        type: 'desktop',
        platform: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });
      await pingHandoffDevice(localDeviceId);

      const [deviceList, sessions, inc, openTabs] = await Promise.all([
        listHandoffDevices(),
        listHandoffSessions(50),
        listIncomingHandoffs(localDeviceId),
        loadOpenTabsForHandoff(localDeviceId),
      ]);

      const nameById = new Map(deviceList.map((d) => [d.id, d.name]));
      setDevices(
        deviceList.map((d) => {
          const card = mapApiDeviceToCard(d);
          if (d.id === localDeviceId) {
            card.tabs = openTabs.length;
          }
          return card;
        }),
      );
      setHandoffSessions(sessions.map((s) => mapSessionToRow(s, nameById)));
      setIncoming(inc);
    } catch (error) {
      console.error('Failed to fetch handoff data:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load handoff data');
      setDevices([]);
      setHandoffSessions([]);
      setIncoming([]);
    } finally {
      setIsLoading(false);
    }
  }, [localDeviceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('register') === '1') {
      setShowQR(true);
    }
  }, []);

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'desktop':
        return Monitor;
      case 'mobile':
        return Smartphone;
      case 'tablet':
        return Tablet;
      default:
        return Monitor;
    }
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const idToName = useMemo(() => new Map(devices.map((d) => [d.id, d.name])), [devices]);

  const onlineCount = devices.filter((d) => d.status === 'online').length;
  const syncActive = onlineCount >= 2;

  const handleSendTabs = async (targetDeviceId: string) => {
    if (targetDeviceId === localDeviceId) {
      toast.error('Pick another device to send tabs to');
      return;
    }
    try {
      const openTabs = await loadOpenTabsForHandoff(localDeviceId);
      if (openTabs.length === 0) {
        toast.error('No local tabs to send. Imported handoff tabs are excluded.');
        return;
      }
      await sendHandoff({
        source_device_id: localDeviceId,
        target_device_id: targetDeviceId,
        open_tabs: openTabs,
      });
      toast.success(`Sent ${openTabs.length} tab${openTabs.length === 1 ? '' : 's'}`);
      await fetchData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Send failed');
    }
  };

  const handleReceive = async (handoff: HandoffSessionDTO) => {
    const sharedTabs = tabsFromSession(handoff).filter((t) => t.url);
    try {
      await receiveHandoff(handoff.id);

      const sessionId = await resolveWorkspaceSessionId();
      if (sessionId && sharedTabs.length > 0) {
        let imported = 0;
        for (const tab of sharedTabs) {
          try {
            await tabService.createTab(sessionId, { url: tab.url, title: tab.title });
            imported += 1;
          } catch (err) {
            console.warn('Failed to import handoff tab', tab.url, err);
          }
        }
        rememberImportedHandoffTabs(localDeviceId, sharedTabs);
        if (imported > 0) {
          toast.success(`Imported ${imported} tab${imported === 1 ? '' : 's'} into Tab Monitor`);
        } else {
          toast.success('Handoff marked received');
        }
      } else {
        toast.success('Handoff marked received');
      }

      await fetchData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Receive failed');
    }
  };

  return (
    <PageLayout
      title="Tab Handoff"
      description="Seamlessly transfer tabs between your devices with instant synchronization"
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-2">
          <Button
            variant="primary"
            leftIcon={<QrCode className="h-5 w-5" />}
            onClick={() => setShowQR(!showQR)}
          >
            Connect Device
          </Button>
        </div>
      </motion.div>

      {showQR && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-6"
        >
          <Card gradient className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-khiimori-text-primary">Connect New Device</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowQR(false)}
                aria-label="Close QR code"
              >
                Close
              </Button>
            </div>
            <div className="flex flex-col items-center justify-center py-8">
              <div className="p-4 bg-khiimori-bg-elevated rounded-lg shadow-lg inline-block border border-khiimori-border">
                <QRCode value={pairUrl} size={192} />
              </div>
              <p className="text-center text-sm text-khiimori-text-primary/80 mt-4 max-w-md">
                Scan or open this link on another device signed into the same account. Then use Tab Handoff
                there so both browsers register as handoff devices.
              </p>
              <p className="text-xs text-khiimori-text-primary/60 mt-2 break-all px-4 text-center">{pairUrl}</p>
            </div>
          </Card>
        </motion.div>
      )}

      <div className="mb-8">
        <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">Your Devices</h2>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : devices.length === 0 ? (
          <p className="text-khiimori-text-muted text-sm">
            No devices yet. Open this page on another device (or use Connect Device) so each browser
            registers itself.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {devices.map((device, index) => {
              const DeviceIcon = getDeviceIcon(device.type);
              const isLocal = device.id === localDeviceId;

              return (
                <motion.div
                  key={device.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedDevice(device)}
                  className="cursor-pointer"
                  role="presentation"
                >
                  <Card hover className={selectedDevice?.id === device.id ? 'ring-2 ring-primary-500' : ''}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 bg-gradient-primary rounded-lg">
                        <DeviceIcon className="h-6 w-6 text-khiimori-text-primary" />
                      </div>
                      <div
                        className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                          device.status === 'online'
                            ? 'bg-success-500/20 text-success-400'
                            : 'bg-khiimori-bg-elevated/10 text-khiimori-text-muted'
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${device.status === 'online' ? 'bg-success-500' : 'bg-khiimori-bg-tertiary'}`}
                        />
                        <span>{device.status}</span>
                      </div>
                    </div>

                    <h3 className="font-bold text-khiimori-text-primary mb-2">
                      {device.name}
                      {isLocal ? (
                        <span className="ml-2 text-xs font-normal text-khiimori-text-muted">(you)</span>
                      ) : null}
                    </h3>

                    <div className="space-y-2 text-sm text-khiimori-text-muted">
                      <div className="flex items-center space-x-2">
                        <Link className="h-4 w-4" />
                        <span>{device.tabs > 0 ? `${device.tabs} tabs` : 'Handoff target'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>Last seen: {formatRelativeTime(device.lastSeen)}</span>
                      </div>
                    </div>

                    {device.status === 'online' && !isLocal && (
                      <Button
                        variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleSendTabs(device.id);
                        }}
                        className="w-full mt-4"
                      >
                        Send Tabs
                      </Button>
                    )}
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {incoming.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">Incoming</h2>
          <div className="space-y-4">
            {incoming.map((h) => {
              const sharedTabs = tabsFromSession(h);
              return (
              <Card key={h.id} hover>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <Download className="mt-1 h-8 w-8 shrink-0 text-info-400" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-khiimori-text-primary">
                        From {idToName.get(h.source_device) ?? h.source_device} · {h.status}
                      </p>
                      <p className="text-sm text-khiimori-text-muted">
                        {sharedTabs.length} tab{sharedTabs.length === 1 ? '' : 's'} to import
                      </p>
                      <HandoffTabList tabs={sharedTabs} />
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => void handleReceive(h)}
                    size="sm"
                    className="shrink-0"
                  >
                    Import tabs
                  </Button>
                </div>
              </Card>
            );
            })}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">Recent Handoffs</h2>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : handoffSessions.length === 0 ? (
          <p className="text-khiimori-text-muted text-sm">No handoff sessions yet.</p>
        ) : (
          <div className="space-y-4">
            {handoffSessions.map((session, index) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card hover>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="p-3 bg-gradient-primary rounded-lg">
                        <ArrowLeftRight className="h-6 w-6 text-khiimori-text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1 flex-wrap">
                          <span className="font-semibold text-khiimori-text-primary">{session.fromDevice}</span>
                          <ArrowLeftRight className="h-4 w-4 text-khiimori-text-secondary" />
                          <span className="font-semibold text-khiimori-text-primary">{session.toDevice}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-khiimori-bg-elevated dark:bg-khiimori-bg-elevated text-khiimori-text-muted">
                            {session.status}
                          </span>
                        </div>
                        <div className="text-sm text-khiimori-text-muted">
                          {session.tabs.length} tabs · {formatRelativeTime(session.timestamp)}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {session.tabs.map((tab, idx) => (
                            <span
                              key={idx}
                              className="max-w-full truncate px-2 py-0.5 bg-info-500/20 text-info-400 rounded text-xs"
                              title={tab.url || tab.title}
                            >
                              {tab.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {session.status === 'received' && (
                      <CheckCircle className="h-6 w-6 text-success-400 shrink-0" />
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8"
      >
        <Card
          className={
            syncActive
              ? 'bg-gradient-to-r from-success-500/20 to-success-500/10 border-success-500/20'
              : 'bg-gradient-to-r from-warning-500/20 to-warning-500/10 border-warning-500/20'
          }
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className={`p-3 rounded-lg ${
                  syncActive
                    ? 'bg-success-500'
                    : 'bg-warning-500'
                }`}
              >
                <Signal className="h-6 w-6 text-khiimori-text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-khiimori-text-primary mb-1">
                  {syncActive ? 'Sync active' : 'Waiting for devices'}
                </h3>
                <p className="text-sm text-khiimori-text-muted">
                  {syncActive
                    ? `${onlineCount} devices online — you can send handoffs between them.`
                    : 'Register at least two online devices to hand off tabs.'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div
                className={`w-2 h-2 rounded-full ${syncActive ? 'bg-success-500 animate-pulse' : 'bg-warning-500'}`}
              />
              <span
                className={`text-sm font-medium ${
                  syncActive ? 'text-success-400' : 'text-warning-400'
                }`}
              >
                {syncActive ? 'Ready' : 'Setup'}
              </span>
            </div>
          </div>
        </Card>
      </motion.div>
    </PageLayout>
  );
};

export default TabHandoff;
